import _ from 'lodash'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import apis from 'browser/app/models/apis'
import {
  LoadingSpinner,
  LoadingSpinnerSize,
} from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { isUUIDValid } from 'shared-libs/helpers/utils'
import type { Entity } from 'shared-libs/models/entity'
import type { Edge, Quantity } from 'shared-libs/generated/server-types/entity'
import type { ESignature } from 'shared-libs/generated/server-types/entity/definitions/eSignature'

interface IChangeSetPatch {
  op: string
  path: string
  value?: unknown
}

// A patch can contain partial, unvalidated data from any of these schema shapes.
type UnvalidatedPatchValue<T> = { [K in keyof T]?: unknown }
type IPatchEdge = UnvalidatedPatchValue<Pick<Edge, 'entityId' | 'displayName'>>
type IPatchQuantity = UnvalidatedPatchValue<Quantity>
type IPatchESignature = UnvalidatedPatchValue<Pick<ESignature, 'signed' | 'signer' | 'signedDate'>>
type IPatchNamedValue = UnvalidatedPatchValue<Pick<Edge, 'displayName'>>
type IPatchValueObject = IPatchEdge | IPatchQuantity | IPatchESignature | IPatchNamedValue

const isPatchValueObject = (value: unknown): value is IPatchValueObject => _.isObject(value)

interface IEventChangeSetListProps extends IBaseProps {
  entity: any
  changeSet: any // The specific changeSet object (whether from root or result)
  showEdgeActivity?: boolean
  /**
   * If true, displays the raw JSON path (e.g. "data.custom")
   * when a human-readable schema label cannot be found.
   */
  fallbackToRawPath?: boolean
}

/**
 * EventChangeSetList
 *
 * A reusable component responsible for rendering the list of changes (JSON patches)
 * within a ChangeSet. It encapsulates the logic for:
 * - Parsing and formatting patch operations (add, remove, replace).
 * - Resolving entity references (UUIDs) to human-readable display names via the store.
 * - Filtering fields based on entity whitelists and blacklists.
 *
 * This is used via composition in both `ChangeSetItem` (Standard Audit) and
 * `AiBlockUsageEventItem` (AI Execution Log) to share rendering logic.
 */
export class EventChangeSetList extends React.Component<IEventChangeSetListProps, any> {
  store: any
  loadingCount: number

  constructor(props: IEventChangeSetListProps) {
    super(props)
    this.store = apis.getStore()
    this.loadingCount = 0
  }

  public render() {
    const { changeSet } = this.props
    const { applyPatch = [] }: { applyPatch?: IChangeSetPatch[] } = changeSet

    const childNodes = [
      ..._.map(applyPatch, (patch, index) => this.renderPatch(patch, index)),
      this.renderMixinPatch(),
    ].filter(Boolean)

    if (this.loadingCount > 0) {
      return <LoadingSpinner key="1" size={LoadingSpinnerSize.XS} />
    }

    return <>{childNodes}</>
  }

  private renderPatch(patch: IChangeSetPatch, index: number) {
    const content = this.renderPatchContent(patch, index)
    if (content) {
      return (
        <span key={index}>
          {content} <br />
        </span>
      )
    }
  }

  private renderPatchContent(patch: IChangeSetPatch, index: number) {
    const { entity } = this.props
    const path = _.compact(patch.path.split('/'))
    _.remove(path, (value) => value === '-')

    if (_.includes(path, 'mixins') && _.includes(path, 'active')) {
      return null
    }

    const isWhiteListed = this.isWhiteListed(entity, path)
    const isBlackListed = this.isBlackListed(path)
    if (!isWhiteListed || isBlackListed) {
      return null
    }
    const label = this.createReadableLabel(path)
    const value = this.resolveValue(patch.value, label, patch.path)
    return this.formatPatchString(patch.op, label, value, index, path, patch)
  }

  private renderMixinPatch() {
    const { entity, changeSet } = this.props

    if (!changeSet.mixinPatch) {
      return
    }

    const { mixinPatch } = changeSet
    const { active, inactive } = mixinPatch
    if (_.isEmpty(active) && _.isEmpty(inactive)) {
      return
    }
    const activeDisplayName = _.map(active, (mixin) => mixin.displayName)
    const inActiveDisplayName = _.map(inactive, (mixin) => mixin.displayName)
    if (!_.isEmpty(activeDisplayName) && _.isEmpty(inActiveDisplayName)) {
      return (
        <span>
          {' '}
          Updated {entity.displayName} to {_.join(activeDisplayName, ', ')} <br />
        </span>
      )
    }
    if (_.isEmpty(activeDisplayName) && !_.isEmpty(inActiveDisplayName)) {
      return (
        <span>
          {' '}
          Removed {_.join(inActiveDisplayName, ', ')} from {entity.displayName} <br />
        </span>
      )
    }
    return (
      <span>
        {' '}
        Updated {entity.displayName} from {_.join(inActiveDisplayName, ', ')} to{' '}
        {_.join(activeDisplayName, ', ')} <br />
      </span>
    )
  }

  private createReadableLabel(path: string[]): string {
    const { entity } = this.props
    const labels = _.map(path, (key, index) => {
      const schemaEntity = entity.resolveSubschemaByValuePath(_.take(path, Number(index) + 1))
      if (!schemaEntity) {
        console.warn(`Invalid path=${path.join('.')}`)
      }
      return _.get(schemaEntity, 'schema.label', '')
    })
    return _.compact(labels).join(' ')
  }

  private resolveValue(
    value: unknown,
    label: string | null = '',
    path: string | string[]
  ): string | undefined {
    if (!value && !_.isBoolean(value)) {
      return ''
    } else if (_.includes(path, 'entityId')) {
      const entity = this.resolveEntity(value)
      return entity?.displayName
    } else if (_.isArray(value)) {
      const values = _.map(value, (item) => this.resolveValue(item, null, path))
      return _.compact(values).join(', ')
    } else if (isPatchValueObject(value)) {
      if ('entityId' in value && value.entityId) {
        const entity = this.resolveEntity(value.entityId)
        return entity?.displayName
      }
      if ('value' in value && value.value && 'unit' in value && value.unit) {
        return `${value.value} ${value.unit}`
      }
      if ('displayName' in value && value.displayName) {
        return String(value.displayName)
      }
      if (this.isESignatureType(value)) {
        return `${value.signer} electronically signed as the ${label}`
      }
      return ''
    }
    return String(value)
  }

  private resolveEntity(entityId: unknown) {
    if (!this.props.showEdgeActivity || !isUUIDValid(entityId)) {
      return
    }
    return this.store.getRecord(entityId) || this.loadEntity(entityId)
  }

  private loadEntity(entityId: unknown) {
    this.loadingCount += 1
    this.store.getOrFetchRecord(entityId).then(() => {
      this.loadingCount -= 1
      this.forceUpdate()
    })
  }

  private formatPatchString(
    operation: string,
    label: string,
    value: string | undefined,
    index: number,
    path: string[],
    patch: IChangeSetPatch
  ) {
    const { changeSet } = this.props

    switch (operation) {
      case 'add':
        if (this.isESignatureType(patch.value)) {
          return value
        }
        if (_.isEmpty(value) || _.isEmpty(label)) {
          return this.props.fallbackToRawPath ? `Added ${value} to ${_.join(path, '.')}` : null
        }
        return `Added ${value} to ${label}`
      case 'remove': {
        const rawValue = changeSet?.revertPatch?.[index]?.value
        if (rawValue) {
          const resolvedValue = this.resolveValue(rawValue, null, path)
          if (_.isEmpty(resolvedValue)) return null
          return `Removed ${resolvedValue} from ${label}`
        }
        return `Removed ${label}`
      }
      case 'replace': {
        if (_.includes(path, 'mechanicSignature')) {
          return `Updated Mechanic Signature`
        }
        const rawValue = _.get(changeSet.applyPatch[index], 'value', undefined)
        const rawPrevValue = _.get(changeSet.revertPatch?.[index], 'value', undefined)

        let prevValue = this.resolveValue(rawPrevValue, null, path)
        if (typeof rawValue === 'number') value = _.round(rawValue, 2).toFixed(2)
        if (typeof rawPrevValue === 'number') prevValue = _.round(rawPrevValue, 2).toFixed(2)

        return `Updated ${label} from ${prevValue} to ${value}`
      }
      case 'move':
        return `Moved ${value}`
      case 'copy':
        return ''
      case 'test':
        return `Tested ${value}`
      default:
        return 'Performed unknown operation'
    }
  }

  private isWhiteListed(entity: Pick<Entity, 'namespaces'>, path: string[]): boolean {
    const whiteList = entity.namespaces
    return _.includes(whiteList, path[0])
  }

  private isBlackListed(path: string[]): boolean {
    return (
      _.includes(path, 'projectedRoute') ||
      _.includes(path, 'attachments') ||
      _.includes(path, 'denormalizedProperties') ||
      _.includes(path, 'displayName') ||
      _.includes(path, 'view') ||
      _.includes(path, 'schemas')
    )
  }

  private isESignatureType(value: unknown): value is IPatchESignature {
    return (
      isPatchValueObject(value) &&
      'signed' in value &&
      !!value.signed &&
      'signer' in value &&
      'signedDate' in value &&
      !_.isNil(value.signer) &&
      !_.isNil(value.signedDate)
    )
  }
}
